import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { DOCUMENT_TYPES } from '../../../../constants';
import { onboardingQuestionnaireActions } from '../../../../../../../store';

import type { AppDispatch, RootState } from '../../../../../../../store';
import type { IOnboardingQuestionnaireDocument } from '../../../../types';

export default function useReferralDocuments() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { onboardingQuestionnaireId } = useParams<'onboardingQuestionnaireId'>();
  const allDocuments = useSelector(
    (state: RootState) => state.onboardingQuestionnaire.onboardingQuestionnaireDetails?.documents,
  );
  const uploadDocumentLoading = useSelector(
    (state: RootState) => state.onboardingQuestionnaire.uploadDocumentLoading,
  );
  const deleteDocumentLoading = useSelector(
    (state: RootState) => state.onboardingQuestionnaire.deleteDocumentLoading,
  );

  const documents = useMemo(
    () =>
      allDocuments?.filter(
        (document) => document.document_type === DOCUMENT_TYPES.REFERRAL_REPORT,
      ) ?? [],
    [allDocuments],
  );

  const uploadDocument = useCallback(
    async (file: File, title: string) => {
      if (!onboardingQuestionnaireId) {
        return;
      }
      await dispatch(
        onboardingQuestionnaireActions.uploadOnboardingQuestionnaireDocument({
          onboardingQuestionnaireId,
          data: {
            file,
            title,
            document_type: DOCUMENT_TYPES.REFERRAL_REPORT,
            document_sub_type: null,
          },
          getAccessTokenSilently,
        }),
      );
    },
    [dispatch, getAccessTokenSilently, onboardingQuestionnaireId],
  );

  const deleteDocument = useCallback(
    async (documentId: IOnboardingQuestionnaireDocument['id']) => {
      if (!onboardingQuestionnaireId) {
        return;
      }
      await dispatch(
        onboardingQuestionnaireActions.deleteOnboardingQuestionnaireDocument({
          onboardingQuestionnaireId,
          documentId: String(documentId),
          getAccessTokenSilently,
        }),
      );
    },
    [dispatch, getAccessTokenSilently, onboardingQuestionnaireId],
  );

  return useMemo(
    () => ({
      documents,
      uploadDocument,
      deleteDocument,
      uploadDocumentLoading,
      deleteDocumentLoading,
    }),
    [documents, uploadDocument, deleteDocument, uploadDocumentLoading, deleteDocumentLoading],
  );
}
